import React, { useState, useRef } from 'react';
import { 
  Printer, 
  FileText, 
  Calendar,
  BookOpen,
  CheckCircle2,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { toast } from 'sonner';

const QuickPrintAll = ({ 
  subject, 
  year, 
  weeklyLessons, 
  subjectColor = "blue" 
}) => {
  const [selectedTerm, setSelectedTerm] = useState('term1');
  const [includeSchemePlan, setIncludeSchemePlan] = useState(true);
  const [includeLessonPlans, setIncludeLessonPlans] = useState(true);
  const [isPrinting, setIsPrinting] = useState(false);
  const [progress, setProgress] = useState(0);
  const printRef = useRef(null);

  // Get school name from logged-in user context
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
  const schoolName = currentUser?.school_name || "St Patrick's College";

  // Get year data based on year number — supports both nested (e.g. { year12: { term1: ... } }) 
  // and flat (e.g. { term1: ... }) data formats
  const getYearKey = () => {
    return `year${year}`;
  };

  const yearData = weeklyLessons?.[getYearKey()] || (weeklyLessons?.term1 ? weeklyLessons : {});
  // Support both 'weeks' array (Agriculture) and 'scheme' array (Chemistry)
  // Note: Chemistry data has 'weeks' as number (count) and 'scheme' as array
  const termWeeks = yearData[selectedTerm]?.scheme || yearData[selectedTerm]?.weeks;
  const termScheme = yearData[selectedTerm]?.scheme;
  const termData = Array.isArray(termWeeks) ? termWeeks : (Array.isArray(termScheme) ? termScheme : []);

  const colorClasses = {
    blue: 'bg-blue-600 hover:bg-blue-700',
    green: 'bg-green-600 hover:bg-green-700',
    teal: 'bg-teal-600 hover:bg-teal-700',
    amber: 'bg-amber-600 hover:bg-amber-700',
    indigo: 'bg-indigo-600 hover:bg-indigo-700',
    emerald: 'bg-emerald-600 hover:bg-emerald-700',
    purple: 'bg-purple-600 hover:bg-purple-700',
    rose: 'bg-rose-600 hover:bg-rose-700'
  };

  const borderColors = {
    blue: '#2563eb',
    green: '#16a34a',
    teal: '#0d9488',
    amber: '#d97706',
    indigo: '#4f46e5',
    emerald: '#059669',
    purple: '#9333ea',
    rose: '#e11d48'
  };

  // Generate lesson plan for a week
  const generateWeekLessonPlan = (weekData) => {
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
    const periods = {
      Monday: [{ type: 'double', duration: '80 min' }],
      Tuesday: [{ type: 'single', duration: '40 min' }],
      Wednesday: [{ type: 'single', duration: '40 min' }],
      Thursday: [{ type: 'single', duration: '40 min' }],
      Friday: [{ type: 'revision', duration: '40 min' }]
    };

    return days.map((day, idx) => ({
      day,
      period: periods[day][0],
      topic: weekData.topic,
      objectives: Array.isArray(weekData.learningOutcomes) 
        ? weekData.learningOutcomes[Math.min(idx, weekData.learningOutcomes.length - 1)]
        : weekData.learningOutcomes,
      activities: Array.isArray(weekData.activities)
        ? weekData.activities[Math.min(idx, weekData.activities.length - 1)]
        : weekData.activities,
      resources: Array.isArray(weekData.resources)
        ? weekData.resources.join(', ')
        : weekData.resources,
      assessment: day === 'Friday' ? weekData.assessment : 'Observation & questioning'
    }));
  };

  // Handle print all
  const handlePrintAll = async () => {
    if (termData.length === 0) {
      toast.error('No data available for selected term');
      return;
    }

    setIsPrinting(true);
    setProgress(0);

    // Simulate progress for UX
    const interval = setInterval(() => {
      setProgress(prev => Math.min(prev + 10, 90));
    }, 100);

    // Small delay to let UI update
    await new Promise(resolve => setTimeout(resolve, 200));

    // Generate print content
    const printWindow = window.open('', '_blank');
    const termName = selectedTerm.replace('term', 'Term ');
    
    let htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${subject} Year ${year} ${termName} - Complete Teaching Pack</title>
        <style>
          @media print {
            @page { margin: 1cm; size: A4; }
            .page-break { page-break-before: always; }
          }
          body { 
            font-family: Arial, sans-serif; 
            font-size: 11pt;
            line-height: 1.4;
            color: #000;
          }
          .header { 
            text-align: center; 
            border-bottom: 3px solid ${borderColors[subjectColor] || '#2563eb'};
            padding-bottom: 10px;
            margin-bottom: 15px;
          }
          .school-name { font-size: 14pt; font-weight: bold; }
          .doc-title { font-size: 12pt; font-weight: bold; margin: 8px 0; }
          .doc-info { font-size: 10pt; color: #666; }
          
          /* Scheme of Work Styles */
          .scheme-table { 
            width: 100%; 
            border-collapse: collapse; 
            margin: 15px 0;
            font-size: 9pt;
          }
          .scheme-table th, .scheme-table td { 
            border: 1px solid #ddd; 
            padding: 6px 8px; 
            text-align: left;
            vertical-align: top;
          }
          .scheme-table th { 
            background: ${borderColors[subjectColor] || '#2563eb'}; 
            color: white;
            font-weight: bold;
          }
          .scheme-table tr:nth-child(even) { background: #f9f9f9; }
          
          /* Lesson Plan Styles */
          .lesson-plan { 
            border: 1px solid #ddd; 
            margin: 15px 0;
            padding: 15px;
          }
          .lesson-header { 
            background: ${borderColors[subjectColor] || '#2563eb'}; 
            color: white;
            padding: 10px;
            margin: -15px -15px 15px -15px;
          }
          .lesson-header h3 { margin: 0; font-size: 12pt; }
          .lesson-header p { margin: 5px 0 0 0; font-size: 10pt; opacity: 0.9; }
          .lesson-grid { 
            display: grid; 
            grid-template-columns: 1fr 1fr; 
            gap: 10px;
            margin-bottom: 10px;
          }
          .lesson-item { 
            background: #f5f5f5; 
            padding: 8px; 
            border-radius: 4px;
          }
          .lesson-label { 
            font-weight: bold; 
            font-size: 9pt; 
            color: #666;
            margin-bottom: 3px;
          }
          .lesson-value { font-size: 10pt; }
          .daily-plan { margin-top: 15px; }
          .daily-plan table { width: 100%; border-collapse: collapse; font-size: 9pt; }
          .daily-plan th, .daily-plan td { 
            border: 1px solid #ddd; 
            padding: 6px; 
            text-align: left;
          }
          .daily-plan th { background: #f0f0f0; }
          
          .footer { 
            text-align: center; 
            font-size: 9pt; 
            color: #999;
            margin-top: 20px;
            padding-top: 10px;
            border-top: 1px solid #ddd;
          }
        </style>
      </head>
      <body>
    `;

    // Add Scheme of Work if selected
    if (includeSchemePlan) {
      htmlContent += `
        <div class="header">
          <div class="school-name">${schoolName.toUpperCase()}</div>
          <div class="doc-title">${subject.toUpperCase()} - YEAR ${year}</div>
          <div class="doc-title">${termName.toUpperCase()} SCHEME OF WORK</div>
          <div class="doc-info">Academic Year 2026</div>
        </div>
        
        <table class="scheme-table">
          <thead>
            <tr>
              <th style="width: 5%">Week</th>
              <th style="width: 12%">Strand</th>
              <th style="width: 18%">Topic</th>
              <th style="width: 20%">Learning Outcomes</th>
              <th style="width: 15%">Activities</th>
              <th style="width: 15%">Resources</th>
              <th style="width: 15%">Assessment</th>
            </tr>
          </thead>
          <tbody>
      `;

      termData.forEach(week => {
        const outcomes = Array.isArray(week.learningOutcomes) 
          ? week.learningOutcomes.join('; ')
          : week.learningOutcomes || '-';
        const activities = Array.isArray(week.activities) 
          ? week.activities.join('; ')
          : week.activities || '-';
        const resources = Array.isArray(week.resources) 
          ? week.resources.join(', ')
          : week.resources || '-';

        htmlContent += `
          <tr>
            <td>${week.week}</td>
            <td>${week.strand || '-'}</td>
            <td><strong>${week.topic}</strong></td>
            <td>${outcomes}</td>
            <td>${activities}</td>
            <td>${resources}</td>
            <td>${week.assessment || '-'}</td>
          </tr>
        `;
      });

      htmlContent += `
          </tbody>
        </table>
        <div class="footer">
          Scheme of Work generated on ${new Date().toLocaleDateString()}
        </div>
      `;
    }

    // Add Lesson Plans if selected
    if (includeLessonPlans) {
      termData.forEach((week, idx) => {
        const weekPlan = generateWeekLessonPlan(week);
        
        htmlContent += `
          <div class="${(includeSchemePlan || idx > 0) ? 'page-break' : ''}">
            <div class="lesson-plan">
              <div class="lesson-header">
                <h3>Week ${week.week} Lesson Plan</h3>
                <p>${subject} - Year ${year} - ${termName}</p>
              </div>
              
              <div class="lesson-grid">
                <div class="lesson-item">
                  <div class="lesson-label">TOPIC</div>
                  <div class="lesson-value">${week.topic}</div>
                </div>
                <div class="lesson-item">
                  <div class="lesson-label">STRAND</div>
                  <div class="lesson-value">${week.strand || '-'}</div>
                </div>
                <div class="lesson-item">
                  <div class="lesson-label">LEARNING OUTCOMES</div>
                  <div class="lesson-value">${Array.isArray(week.learningOutcomes) ? week.learningOutcomes.join('; ') : week.learningOutcomes || '-'}</div>
                </div>
                <div class="lesson-item">
                  <div class="lesson-label">RESOURCES</div>
                  <div class="lesson-value">${Array.isArray(week.resources) ? week.resources.join(', ') : week.resources || '-'}</div>
                </div>
              </div>
              
              <div class="daily-plan">
                <table>
                  <thead>
                    <tr>
                      <th style="width: 12%">Day</th>
                      <th style="width: 10%">Period</th>
                      <th style="width: 25%">Activities</th>
                      <th style="width: 28%">Teaching Notes</th>
                      <th style="width: 25%">Assessment</th>
                    </tr>
                  </thead>
                  <tbody>
        `;
        
        weekPlan.forEach(day => {
          htmlContent += `
            <tr>
              <td><strong>${day.day}</strong></td>
              <td>${day.period.type}<br/>(${day.period.duration})</td>
              <td>${day.activities}</td>
              <td>${day.objectives}</td>
              <td>${day.assessment}</td>
            </tr>
          `;
        });
        
        htmlContent += `
                  </tbody>
                </table>
              </div>
              
              <div class="footer">
                Week ${week.week} Lesson Plan - Generated ${new Date().toLocaleDateString()}
              </div>
            </div>
          </div>
        `;
      });
    }

    htmlContent += `</body></html>`;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    clearInterval(interval);
    setProgress(100);
    
    // Print after a short delay to ensure content is loaded
    setTimeout(() => {
      printWindow.print();
      setIsPrinting(false);
      setProgress(0);
      toast.success(`${termName} teaching pack ready to print!`);
    }, 500);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Printer className="h-5 w-5" />
          Quick Print All
        </CardTitle>
        <CardDescription>
          Print complete term pack including Scheme of Work and all weekly Lesson Plans
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Settings */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-medium mb-2 block">Select Term</label>
            <Select value={selectedTerm} onValueChange={setSelectedTerm}>
              <SelectTrigger data-testid="quick-print-term-select">
                <SelectValue placeholder="Select term" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="term1">Term 1</SelectItem>
                <SelectItem value="term2">Term 2</SelectItem>
                <SelectItem value="term3">Term 3</SelectItem>
                <SelectItem value="term4">Term 4</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-3">
            <label className="text-sm font-medium block">Include in Print</label>
            <div className="space-y-2">
              <div 
                className="flex items-center space-x-3 p-2 rounded border cursor-pointer hover:bg-gray-50"
                onClick={() => setIncludeSchemePlan(!includeSchemePlan)}
              >
                <Checkbox 
                  checked={includeSchemePlan}
                  onCheckedChange={setIncludeSchemePlan}
                />
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <span className="text-sm">Scheme of Work</span>
                </div>
              </div>
              <div 
                className="flex items-center space-x-3 p-2 rounded border cursor-pointer hover:bg-gray-50"
                onClick={() => setIncludeLessonPlans(!includeLessonPlans)}
              >
                <Checkbox 
                  checked={includeLessonPlans}
                  onCheckedChange={setIncludeLessonPlans}
                />
                <div className="flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-gray-500" />
                  <span className="text-sm">Weekly Lesson Plans ({termData.length} weeks)</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="font-medium">Print Summary</span>
            <Badge variant="outline">
              {selectedTerm.replace('term', 'Term ')}
            </Badge>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle2 className={`h-4 w-4 ${includeSchemePlan ? 'text-green-500' : 'text-gray-300'}`} />
              <span className={includeSchemePlan ? '' : 'text-gray-400'}>
                1 Scheme of Work
              </span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className={`h-4 w-4 ${includeLessonPlans ? 'text-green-500' : 'text-gray-300'}`} />
              <span className={includeLessonPlans ? '' : 'text-gray-400'}>
                {termData.length} Lesson Plans
              </span>
            </div>
          </div>
          <div className="mt-3 text-xs text-gray-500">
            Estimated pages: ~{(includeSchemePlan ? 1 : 0) + (includeLessonPlans ? termData.length : 0)} pages
          </div>
        </div>

        {/* Progress Bar */}
        {isPrinting && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span>Generating print preview...</span>
              <span>{progress}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        )}

        {/* Print Button */}
        <Button 
          onClick={handlePrintAll}
          disabled={isPrinting || (!includeSchemePlan && !includeLessonPlans)}
          className={`w-full ${colorClasses[subjectColor] || colorClasses.blue}`}
          size="lg"
          data-testid="quick-print-all-btn"
        >
          {isPrinting ? (
            <>
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              Preparing Print...
            </>
          ) : (
            <>
              <Printer className="h-5 w-5 mr-2" />
              Print Complete Term Pack
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

export default QuickPrintAll;
